import { type ProductizedServiceCardDataWrapper$data } from '#app/__generated__/relay/ProductizedServiceCardDataWrapper.graphql.js';
import { MountedPortal } from '#app/components/MountedPortal/MountedPortal.js';
import { animated } from '#app/components/Primitives/Animated.js';
import { ProjectCover } from '#app/components/ProjectCover/ProjectCover.js';
import { springAnimation } from '#app/utilities/animation.js';
import { ProjectOutlineIcon } from '@contra/icons/icons';
import { getRoute } from '@contra/routes';
import { AnimatePresence } from 'framer-motion';
import { useCallback, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { css, styled } from 'styled-components';

const ProjectPopover = styled(animated.div)`
  ${({ theme }) => css`
    width: 280px;
    padding: 16px;
    border-radius: 10px;
    background-color: ${theme.colors.white40};
    box-shadow: ${theme.boxShadow.toast};
  `}
`;

const LinkedPortfolioProjectLink = styled.a`
  cursor: pointer;
`;

const LinkedPortfolioProjectIcon = styled(ProjectOutlineIcon)`
  ${({ theme }) => css`
    color: ${theme.colorsV2.gray60};
  `}
`;

type ProductizedServiceCardLinkedProjectProps = {
  readonly portfolioProject: ProductizedServiceCardDataWrapper$data['portfolioProjects']['edges'][0]['node'];
};

export const ProductizedServiceCardLinkedProject = ({
  portfolioProject,
}: ProductizedServiceCardLinkedProjectProps): JSX.Element => {
  const [showPopper, setShowPopper] = useState(false);

  const referenceElement = useRef<HTMLAnchorElement>(null);
  const popperElement = useRef<HTMLDivElement>(null);

  const { attributes, styles } = usePopper(
    referenceElement.current,
    popperElement.current,
    {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [-16, 8],
          },
        },
      ],
      placement: 'top-start',
      strategy: 'absolute',
    },
  );

  const handleMouseEnter = useCallback(() => {
    setShowPopper(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowPopper(false);
  }, []);

  return (
    <LinkedPortfolioProjectLink
      aria-label={portfolioProject.title}
      href={getRoute('/p/@slug', {
        args: {
          slug: portfolioProject.slug,
        },
      })}
      key={portfolioProject.id}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={referenceElement}
    >
      <LinkedPortfolioProjectIcon size={16} />

      <MountedPortal>
        <AnimatePresence>
          <div
            ref={popperElement}
            style={styles['popper']}
            {...attributes['popper']}
          >
            {showPopper ? (
              <ProjectPopover
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                initial={{ opacity: 0, y: -8 }}
                transition={springAnimation.veryFast}
              >
                <ProjectCover
                  image={portfolioProject.cover.image}
                  textCoverStyle={portfolioProject.cover.style}
                  title={portfolioProject.title}
                />
              </ProjectPopover>
            ) : null}
          </div>
        </AnimatePresence>
      </MountedPortal>
    </LinkedPortfolioProjectLink>
  );
};
